import { useState, useEffect, useCallback, useRef } from 'react';
import {
  adminAPI,
  ProcessStatus,
  PendingWork,
  DataFreshness,
  DiskUsage,
  ErrorLogEntry,
} from '../services/admin';

interface ProcessPollingData {
  processes: ProcessStatus[];
  pendingWork: PendingWork | null;
  freshness: DataFreshness | null;
  diskUsage: DiskUsage | null;
  errors: ErrorLogEntry[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

interface UseProcessPollingOptions {
  intervalMs?: number;
  fastIntervalMs?: number;
  pauseWhenHidden?: boolean;
}

export function useProcessPolling(options: UseProcessPollingOptions = {}) {
  const {
    intervalMs = 10000, // 10 seconds when idle
    fastIntervalMs = 5000, // 5 seconds when processes running
    pauseWhenHidden = true,
  } = options;

  const [data, setData] = useState<ProcessPollingData>({
    processes: [],
    pendingWork: null,
    freshness: null,
    diskUsage: null,
    errors: [],
    isLoading: true,
    error: null,
    lastUpdated: null,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef(true);

  const fetchData = useCallback(async () => {
    try {
      const [processesRes, pendingWork, freshness, diskUsage, errorsRes] = await Promise.all([
        adminAPI.getProcessStatuses(),
        adminAPI.getPendingWork(),
        adminAPI.getDataFreshness(),
        adminAPI.getDiskUsage(),
        adminAPI.getRecentErrors(10),
      ]);

      setData({
        processes: processesRes.processes,
        pendingWork,
        freshness,
        diskUsage,
        errors: errorsRes.errors,
        isLoading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (err) {
      setData((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Blad podczas pobierania danych',
      }));
    }
  }, []);

  const hasRunningProcess = useCallback(() => {
    return data.processes.some((p) => p.status === 'running');
  }, [data.processes]);

  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const interval = hasRunningProcess() ? fastIntervalMs : intervalMs;
    intervalRef.current = setInterval(() => {
      if (!pauseWhenHidden || isVisibleRef.current) {
        fetchData();
      }
    }, interval);
  }, [fetchData, hasRunningProcess, intervalMs, fastIntervalMs, pauseWhenHidden]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Start polling after initial fetch
  useEffect(() => {
    if (!data.isLoading) {
      startPolling();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [data.isLoading, startPolling]);

  // Update interval when running state changes
  useEffect(() => {
    if (!data.isLoading) {
      startPolling();
    }
  }, [hasRunningProcess, startPolling, data.isLoading]);

  // Visibility change handler
  useEffect(() => {
    if (!pauseWhenHidden) return;

    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      if (!document.hidden) {
        // Refetch immediately when becoming visible
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pauseWhenHidden, fetchData]);

  const refetch = useCallback(() => {
    setData((prev) => ({ ...prev, isLoading: true }));
    fetchData();
  }, [fetchData]);

  return {
    ...data,
    refetch,
    hasRunningProcess: hasRunningProcess(),
  };
}
